import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CampaignEmptyState } from '@/components/campaign/campaign-empty-state';
import { CampaignVisibilityWarning } from '@/components/campaign/campaign-visibility-warning';
import { PageErrorState } from '@/components/glitch-ui/empty-state';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { useAtomValue, useSetAtom } from 'jotai';
import { campaignDataAtom, metaCampaignDataAtom, campaignsDataAtom, campaignsMetaAtom, isLoadingCampaignsAtom, organizationDataAtom, googleAdsAccountAtom, campaignsErrorAtom } from '@/store';
import { metaCampaignsQueryOptions } from '@/lib/query-options';
import { useTestMode } from '@/hooks/useTestMode';
import { useQuery } from '@tanstack/react-query';
import { useRefetchCampaigns } from '@/hooks/useRefetchCampaign';
// import { queryOptions } from '@tanstack/react-query';  // TODO: Re-enable for brand campaign feature
import { useEffect } from 'react';
import { Megaphone } from 'lucide-react';
// import { getBusinessesByOrganization } from '@/lib/apis';  // TODO: Re-enable for brand campaign feature
// import type { Business } from '@/lib/types';  // TODO: Re-enable for brand campaign feature

// This route redirects to the first available campaign

export const Route = createFileRoute('/_authenticated/_layout/campaigns/')({
  component: Campaigns,
  staticData: {
    title: 'Campaigns',
    icon: Megaphone,
  },
});

function Campaigns() {
  const navigate = useNavigate({ from: '/' });
  const campaigns = useAtomValue(campaignsDataAtom);
  const isLoadingCampaigns = useAtomValue(isLoadingCampaignsAtom);
  const lastSelectedCampaign = useAtomValue(campaignDataAtom);
  const lastSelectedMetaSlug = useAtomValue(metaCampaignDataAtom);
  const setCampaignData = useSetAtom(campaignDataAtom);
  const campaignsMeta = useAtomValue(campaignsMetaAtom);
  const organizationData = useAtomValue(organizationDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const campaignsError = useAtomValue(campaignsErrorAtom);
  // #5325: the campaigns fetch is no longer an atom closure — retry via query invalidation.
  const { refetch: refetchCampaigns } = useRefetchCampaigns();
  const isTestMode = useTestMode();
  // Meta campaigns live on the same unified surface. The index must account for them too —
  // otherwise a Meta-only org (no Google campaigns) falls through to the "No Campaigns" empty
  // state even though Meta campaigns exist.
  // #6257: gated behind test mode until Meta App Review clears — the disabled query returns []
  // so every Meta row on the unified surface vanishes without touching the render path.
  const { data: metaCampaigns = [], isLoading: isLoadingMetaCampaigns } = useQuery({
    ...metaCampaignsQueryOptions(),
    enabled: isTestMode,
  });
  // TODO: Re-enable business check for brand campaign feature in next release
  // Fetch business data to check if brand has been set up
  // const { data: businesses = [] } = useSuspenseQuery(
  //   queryOptions({
  //     queryKey: ['businesses', organizationData?.id],
  //     queryFn: () => {
  //       if (!organizationData?.id) return Promise.resolve([]);
  //       return getBusinessesByOrganization(organizationData.id);
  //     },
  //     enabled: !!organizationData?.id,
  //   })
  // );
  // const business: Business | undefined = businesses[0];

  // Check if campaigns query prerequisites are met (same as parent route)
  const queryEnabled = !!organizationData?.id && !!googleAdsAccount?.googleads_account_id;

  // Check if campaigns are inaccessible based on meta data
  // Only trust meta data if query has run AND we actually have empty campaigns
  const showVisibilityWarning = queryEnabled && !isLoadingCampaigns &&
    campaigns.length === 0 && campaignsMeta?.reason === 'campaigns_inaccessible';

  // Check if user has no Google Ads accounts at all (not just unselected)

  const nonExternalCampaigns = campaigns.filter(c => c.origin !== 'external');

  // Campaign redirect logic. Restore whichever campaign was viewed last, regardless of platform
  // (#4660). Because selecting a Meta campaign clears the Google selection and vice-versa, a non-null
  // last-selected Meta id means Meta was the most recent pick — restore it even when Google campaigns
  // also exist. Mirrors the Google restore via campaignDataAtom; falls back to the first campaign.
  useEffect(() => {
    const restoredMetaCampaign = lastSelectedMetaSlug != null
      ? metaCampaigns.find(m => m.slug === lastSelectedMetaSlug)
      : undefined;

    if (restoredMetaCampaign) {
      setCampaignData(null);
      navigate({ to: '/campaigns/meta/$metaCampaignSlug', params: { metaCampaignSlug: restoredMetaCampaign.slug } });
    } else if (nonExternalCampaigns.length) {
      const targetCampaign = lastSelectedCampaign &&
        nonExternalCampaigns.find(c => c.slug === lastSelectedCampaign.slug) || nonExternalCampaigns[0];

      // Set the campaign data to enable useAreAllTasksCompleted hook
      setCampaignData(targetCampaign);

      // Redirect to campaign page (shows Advanced tab)
      navigate({ to: `/campaigns/google/${targetCampaign.slug}` });
    } else if (metaCampaigns.length > 0) {
      // No Google campaigns but Meta campaigns exist — land on the first Meta campaign's detail,
      // where the unified sidebar (Google + Meta) lives. Without this the index shows the empty state.
      setCampaignData(null);
      navigate({ to: '/campaigns/meta/$metaCampaignSlug', params: { metaCampaignSlug: metaCampaigns[0].slug } });
    } else {
      setCampaignData(null);
    }
  }, [nonExternalCampaigns, metaCampaigns, lastSelectedCampaign, lastSelectedMetaSlug, navigate, setCampaignData]);

  // Don't show any campaigns content if organization is not set up or still loading
  if (!organizationData?.id || isLoadingCampaigns || isLoadingMetaCampaigns) {
    return null;
  }

  // Campaigns failed to load (4xx/5xx). Show a page-level error state instead of falling through
  // to the "No Campaigns" empty state, which would wrongly imply the org has no campaigns.
  if (campaignsError) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-3"></div>
        </div>
        <div className="flex-1">
          <PageErrorState
            title="Couldn't Load Campaigns"
            description="This page failed to load. This is a loading error, not missing data, so nothing has been lost. Retry, or contact support if it persists."
            onRetry={() => refetchCampaigns()}
          />
        </div>
      </div>
    );
  }

  // If non-external Glitch campaigns exist, redirect to first campaign
  if (nonExternalCampaigns.length > 0) {
    return null; // useEffect redirect will handle it
  }

  // No Google campaigns, but Meta campaigns exist — the redirect effect sends us to the first Meta
  // campaign. Return null so we don't flash the empty state (or the no-Google-account warning below).
  if (metaCampaigns.length > 0) {
    return null;
  }

  // Campaigns exist but aren't accessible (query ran but meta says inaccessible) → visibility warning.
  if (showVisibilityWarning) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 py-4">
          <CampaignVisibilityWarning />
        </div>
      </div>
    );
  }

  // Show empty state when no campaigns exist — straight to CREATE CAMPAIGN, one linear sequence.
  // Connection state deliberately does NOT surface here (design review on #5958): the wizard
  // picker gates each platform's types on its own connection and carries the connect CTAs.
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3"></div>
      </div>
      <div className="space-y-6">
        <TabCardPanel
          tabs={[
            { id: 'all', label: 'Campaigns', isActive: true },
          ]}
        >
          <CampaignEmptyState />
        </TabCardPanel>
      </div>
    </div>
  );
}
